import calculateDocking from '../actions/calculateDocking';
import type DockingViewState from '../store/schema/DockingViewState';
import getScrollTopForCursor from '../utils/getScrollTopForCursor';
import tryScrollIntoView from '../utils/tryScrollIntoView';
import type { EditorViewState, PluginViewStateAdapter, PluginWithViewState } from 'owa-editor';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import { ContentChangedEvent, IEditor, PluginEvent, PluginEventType } from 'roosterjs-editor-types';
import { getPositionRect } from 'roosterjs-editor-dom';

export default class DockingPlugin implements PluginWithViewState<DockingViewState> {
    private editor: IEditor;
    private scrollThrottlingJob: number;

    // Properties used for non-sticky docking
    private useNonStickyDocking: boolean;
    private observer: MutationObserver;
    private observerConnected: boolean;
    private dockingThrottlingJob: number;

    // +----------------------------------------------+----+
    // |  scrollContainer                             |  ^ |
    // |                                              |  # |
    // |  The scrollable container provides scroll bar|  # |
    // |                                              |  # |
    // |                                              |  # |
    // |   +------------------------------------+     |    |
    // |   |  initialVisiblePart (Optional)     |     |    |
    // |   |                                    |     |    |
    // |   |  When set, will scroll this part   |     |    |
    // |   |  into view initially               |     |    |
    // |   |                                    |     |    |
    // |   |   +---------------------------+    |     |    |
    // |   |   |  dockingTriggerPart       |    |     |    |
    // |   |   |                           |    |     |    |
    // |   |   |  When top or bottom of    |    |     |    |
    // |   |   |  this part is not visible |    |     |    |
    // |   |   |  it will trigger top or   |    |     |    |
    // |   |   |  bottom docking happen    |    |     |    |
    // |   |   |                           |    |     |    |
    // |   |   |                           |    |     |    |
    // |   |   +---------------------------+    |     |    |
    // |   |                                    |     |    |
    // |   |                                    |     |    |
    // |   +------------------------------------+     |    |
    // |                                              |    |
    // |                                              |  v |
    // +---------------------------------------------------+

    constructor(
        public readonly adapter: PluginViewStateAdapter<DockingViewState>,
        public readonly editorAdapter: PluginViewStateAdapter<EditorViewState>,
        private getBottomDockingHeight: () => number
    ) {
        this.useNonStickyDocking = !this.adapter().useStickyDocking;
        if (this.useNonStickyDocking && typeof MutationObserver == 'function') {
            this.observer = new MutationObserver(this.calculateDockingWithThrottling);
        }
    }

    public getName() {
        return 'Docking';
    }

    public initialize(editor: IEditor) {
        this.editor = editor;

        if (this.useNonStickyDocking) {
            this.tryStartObserver();
            lazySubscribeToResizeEvent.importAndExecute(this.calculateDockingWithThrottling);
        }
    }

    public dispose() {
        if (this.useNonStickyDocking) {
            lazyUnsubscribeFromResizeEvent.importAndExecute(this.calculateDockingWithThrottling);
            this.tryStopObserver();

            if (this.dockingThrottlingJob) {
                window.cancelAnimationFrame(this.dockingThrottlingJob);
                this.dockingThrottlingJob = 0;
            }
        }

        this.editor = null;
    }

    public onPluginEvent(event: PluginEvent) {
        if (
            event.eventType == PluginEventType.KeyUp ||
            event.eventType == PluginEventType.KeyPress ||
            (event.eventType == PluginEventType.ContentChanged &&
                (<ContentChangedEvent>event).source != 'SetContent')
        ) {
            if (this.adapter().isBottomDocking) {
                this.scrollCursorIntoView();
            }
        }
        if (this.useNonStickyDocking && event.eventType == PluginEventType.Scroll) {
            this.calculateDockingWithThrottling();
        }
    }

    private scrollCursorIntoView() {
        if (!this.scrollThrottlingJob) {
            this.scrollThrottlingJob = window.setTimeout(() => {
                this.scrollThrottlingJob = 0;
                const scrollContainer = this.getScrollContainer();
                if (scrollContainer) {
                    const containerRect = scrollContainer.getBoundingClientRect();
                    const focusedPosition = this.editor.getFocusedPosition();
                    const cursorRect = focusedPosition && getPositionRect(focusedPosition);
                    // getPositionRect could return a null, make sure to check it before proceeding
                    if (containerRect && cursorRect) {
                        scrollContainer.scrollTop = getScrollTopForCursor(
                            this.adapter(),
                            scrollContainer.scrollTop,
                            containerRect.bottom,
                            cursorRect.bottom,
                            this.getBottomDockingHeight()
                        );
                    }
                }
            }, 100);
        }
    }

    private getScrollContainer() {
        return this.editor?.getScrollContainer();
    }

    // Functions used for non-sticky docking

    private tryStartObserver() {
        const scrollContainer = this.getScrollContainer();

        // TODO: Once we support compose projection window, need to make sure the observer code works well in projection window
        // We can check this.viewState.isPopout to know if this is in popout window
        if (this.observer && !this.observerConnected && scrollContainer) {
            this.observer.observe(scrollContainer, {
                childList: true,
                attributes: true,
                subtree: true,
            });

            tryScrollIntoView(this.editorAdapter(), this.adapter());

            // Do an initial docking calculation
            this.calculateDockingWithThrottling();
            this.observerConnected = true;
        }
    }

    private tryStopObserver() {
        if (this.observer?.disconnect && this.observerConnected) {
            this.observer.disconnect();
            this.observerConnected = false;
        }
    }

    private calculateDockingWithThrottling = () => {
        if (!this.dockingThrottlingJob) {
            this.dockingThrottlingJob = window.requestAnimationFrame(() => {
                this.dockingThrottlingJob = 0;
                // Check if the editor is still before starting the docking calculation
                if (this.editor) {
                    this.onCalculateDocking();
                }
            });
        }
    };

    private onCalculateDocking = () => {
        const scrollContainer = this.getScrollContainer();
        const viewState = this.adapter();
        const dockingTriggerPart = viewState?.dockingTriggerPart;

        if (scrollContainer && dockingTriggerPart) {
            const newWidth = dockingTriggerPart.offsetWidth;
            calculateDocking(
                viewState,
                scrollContainer.scrollTop,
                scrollContainer.scrollLeft,
                scrollContainer.clientHeight,
                scrollContainer.offsetHeight,
                dockingTriggerPart.offsetTop,
                dockingTriggerPart.offsetHeight,
                newWidth,
                this.getBottomDockingHeight()
            );
        }
    };
}
